import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { Button, Paginator } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import {
  useAuthoredThemes,
  useSubmitForReview,
  useWithdrawToPrivate,
} from '@/lib/themes/themes-queries';
import { ThemeCard } from '@/components/themes/theme-card';
import { TutorLibraryTabs } from '@/components/tutor-library-tabs';
import { usePagedList } from '@/lib/use-paged-list';

export function TutorThemesPage() {
  const { user } = useAuth();
  const { data: themes = [], isLoading } = useAuthoredThemes(user?.id);
  const submit = useSubmitForReview();
  const withdraw = useWithdrawToPrivate();
  const { items: pageThemes, paginator } = usePagedList(themes, {
    unit: 'thèmes',
    label: 'Pages des thèmes',
  });

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:py-8">
      <TutorLibraryTabs />
      <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-ink-900 text-[24px] font-bold">Mes thèmes</h1>
          <p className="text-ink-500 font-sans text-[14px]">
            Crée des contextes pour rattacher tes exercices.
          </p>
        </div>
        <Link to="/tutor/themes/new" className="w-full sm:w-auto">
          <Button className="min-h-[2.75rem] w-full sm:w-auto">
            <Plus className="h-4 w-4" /> Créer un thème
          </Button>
        </Link>
      </header>

      {isLoading ? (
        <p className="text-ink-500 text-sm">Chargement…</p>
      ) : themes.length === 0 ? (
        <p className="text-ink-500 text-sm">
          Tu n'as pas encore créé de thème. Commence par le premier !
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {pageThemes.map((t) => (
            <div
              key={t.id}
              className="border-ink-200 flex flex-col gap-3 rounded-2xl border bg-white p-4"
            >
              <ThemeCard theme={t} to={`/themes/${t.slug}`} showVisibility />
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Link
                  to="/tutor/themes/$id/edit"
                  params={{ id: t.id }}
                  className="text-ink-700 border-ink-200 inline-flex min-h-[2.75rem] items-center justify-center rounded-xl border px-4 text-sm font-semibold"
                >
                  Éditer
                </Link>
                {t.visibility === 'private' && (
                  <button
                    onClick={() => submit.mutate(t.id)}
                    disabled={submit.isPending}
                    className="bg-primary-500 hover:bg-primary-600 inline-flex min-h-[2.75rem] items-center justify-center rounded-xl px-4 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    Soumettre pour publication
                  </button>
                )}
                {(t.visibility === 'pending_review' || t.visibility === 'public') && (
                  <button
                    onClick={() => withdraw.mutate(t.id)}
                    disabled={withdraw.isPending}
                    className="text-ink-700 border-ink-200 inline-flex min-h-[2.75rem] items-center justify-center rounded-xl border px-4 text-sm font-semibold disabled:opacity-50"
                  >
                    Repasser en privé
                  </button>
                )}
              </div>
            </div>
          ))}
          <Paginator {...paginator} />
        </div>
      )}
    </div>
  );
}
